import _ from 'lodash'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Drawer from '@mui/material/Drawer'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import { ListWaitingRoomsParams } from '@/core/api/waiting-room'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { isStaff } from '@/utils/type-guards'

import WaitingRoomsSearchForm, { WaitingRoomsSearchFormValues } from './components/WaitingRoomsSearchForm'
import WaitingRoomsTable, { WaitingRoomData } from './components/WaitingRoomsTable'

dayjs.extend(customParseFormat)
dayjs.extend(isBetween)

const sidebarWidth = 300

export function WaitingRoomsPage() {
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const [listWaitingRoomsParams, setListWaitingRoomsParams] = useState<ListWaitingRoomsParams>({})

  const { data: waitingRoomsResponse } = useListWaitingRoomsQuery(
    { query: listWaitingRoomsParams, options: { limit: 0, skip: 0 } },
    { refetchOnMountOrArgChange: true }
  )
  const isDoctor = useMemo(() => isStaff(authUser) && authUser?.status === 'MEDECIN', [authUser])

  const waitingRoomsData = useMemo(
    () =>
      _.compact(
        _.map(waitingRoomsResponse?.items, (waitingRoom) => {
          if (!authUser) return null
          const openingHour = _.find(waitingRoom.openingHours, ({ day }) => day === dayjs().day())
          let isOpening = false
          if (openingHour) {
            const { periods } = openingHour
            isOpening = _.some(periods, (period) => dayjs().isBetween(dayjs(period.open, 'HH:mm'), dayjs(period.close, 'HH:mm')))
          }
          const hasJoinedWaitingRoom = waitingRoom.doctors.includes(authUser._id)
          const allowedActions: WaitingRoomData['allowedActions'] = []
          if (isStaff(authUser) && authUser.status === 'MEDECIN' && !hasJoinedWaitingRoom && !!waitingRoom.network) allowedActions.push('join')
          if (authUser._id === waitingRoom.createdBy) allowedActions.push('access')
          if (hasJoinedWaitingRoom) allowedActions.push('access', 'leave')
          if (isStaff(authUser) && authUser?.status === 'PDS' && isOpening && waitingRoom.doctors.length > 0) allowedActions.push('reserve')
          if (waitingRoom.createdBy === authUser?._id) allowedActions.push('edit', 'delete')

          const isFavorite = isStaff(authUser) && (authUser.favoriteWaitingRooms ?? []).includes(waitingRoom._id)

          return {
            id: waitingRoom._id,
            isFavorite,
            isOpening,
            name: waitingRoom.name,
            network: waitingRoom.network,
            specialities: waitingRoom.specialities.map((speciality) => speciality.name),
            price: waitingRoom.price !== 0 ? waitingRoom.price : undefined,
            allowedActions: _.uniq(allowedActions),
          }
        })
      ),
    [authUser, waitingRoomsResponse]
  )

  const handleSearchWaitingRooms = (values: WaitingRoomsSearchFormValues) => {
    setListWaitingRoomsParams(values)
  }

  return (
    <Box>
      {isLargeScreen && (
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: sidebarWidth,
              boxSizing: 'border-box',
              pb: '20px',
              [theme.breakpoints.up('md')]: {
                position: 'absolute',
              },
            },
          }}
        >
          <Toolbar />
          <WaitingRoomsSearchForm direction="column" onReset={() => setListWaitingRoomsParams({})} onSubmit={handleSearchWaitingRooms} />
        </Drawer>
      )}
      <Box sx={[isLargeScreen && { marginLeft: `${sidebarWidth}px` }]}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h2" sx={{ mt: '30px', mb: '30px' }}>
              {t('page.waitingRooms.title')}
            </Typography>
            <Stack direction="row" spacing={2}>
              {isDoctor && (
                <Button component={Link} to="/waiting-rooms/create" variant="contained">
                  {t('page.waitingRooms.createWaitingRoomButton.label')}
                </Button>
              )}
            </Stack>
          </Stack>
          {!isLargeScreen && (
            <Paper sx={{ mb: '20px', pt: '1px', pb: '15px' }}>
              <WaitingRoomsSearchForm direction="row" onReset={() => setListWaitingRoomsParams({})} onSubmit={handleSearchWaitingRooms} />
            </Paper>
          )}
          <WaitingRoomsTable data={waitingRoomsData} />
        </Container>
      </Box>
    </Box>
  )
}

export default WaitingRoomsPage
